import React, { useEffect, useRef, useContext, useState } from 'react';
import { MapContainer, Marker, ScaleControl, AttributionControl, ZoomControl } from 'react-leaflet';
import AppContext from '../context/AppContext';
import MapContext from '../context/MapContext';
import NavigationLayer from './layers/NavigationLayer';
import WeatherLayer from './layers/WeatherLayer';
import { createMapHash, formatMapHash } from './util/mapHash';
import 'leaflet-contextmenu';
import 'leaflet-contextmenu/dist/leaflet.contextmenu.css';
import FavoriteLayer from './layers/FavoriteLayer';
import CloudTrackLayer from './layers/CloudTrackLayer';
import LocalClientTrackLayer from './layers/LocalClientTrackLayer';
import MarkerOptions from './markers/MarkerOptions';
import ContextMenu from './components/ContextMenu';
import PoiLayer from './layers/PoiLayer';
import GraphLayer from './layers/GraphLayer';
import { detectGeoByIp, LocationControl } from './components/LocationControl';
import { getInitialView, getPinPointFromUrl } from './util/initialMapView';
import { useWindowSize } from '../util/hooks/useWindowSize';
import CustomTileLayer from './layers/CustomTileLayer';
import ExploreLayer from './layers/ExploreLayer';
import SearchLayer from './layers/SearchLayer';
import MapStateLayer from './layers/MapStateLayer';
import HeightmapLayer from './layers/HeightmapLayer';
import TravelLayer from './layers/TravelLayer';
import HeatmapLayer from './layers/HeatmapLayer';
import ShareFileLayer from './layers/ShareFileLayer';
import TrackAnalyzerLayer from './layers/TrackAnalyzerLayer';
import { useGpxFileDragMapZone } from '../util/hooks/useGpxFileDragZone';
import { Box } from '@mui/material';
import TransportStopsLayer from './layers/TransportStopsLayer';
import MvtDemoLayer from './layers/MvtDemoLayer';
import MvtOsmLayer from './layers/MvtOsmLayer';
import { isMvtTileURL } from './layers/MvtLayerConfig';

const updateMarker = ({ lat, lng, setHoverPoint, hoverPointRef, ctx }) => {
    if (lat) {
        if (hoverPointRef.current) {
            hoverPointRef.current.setLatLng([lat, lng]);
        } else {
            setHoverPoint({ lat, lng });
        }
        ctx.setGraphHighlightedPoint({ lat, lng });
    } else {
        setHoverPoint(null);
        ctx.setGraphHighlightedPoint(null);
    }
};

const OsmAndMap = ({ mainMenuWidth, menuInfoWidth }) => {
    const mapRef = useRef(null);
    const tileLayer = useRef(null);
    const hoverPointRef = useRef(null);

    const [initialView] = useState(getInitialView);

    const [geocodingData, setGeocodingData] = useState(null);
    const [regionData, setRegionData] = useState(null);
    const [width] = useWindowSize();

    const ctx = useContext(AppContext);
    const mtx = useContext(MapContext);
    const [hoverPoint, setHoverPoint] = useState(null);

    const menuMargin = parseFloat(menuInfoWidth) !== 0 ? parseFloat(menuInfoWidth) - 100 : 0;
    const attributionSize = 300;
    const marginLeft = width / 2 - attributionSize + menuMargin;

    const mapDragHandlers = useGpxFileDragMapZone();

    const whenReadyHandler = (event) => {
        const { target: map } = event;
        if (map) {
            if (getPinPointFromUrl()) {
                // leaflet-hash and detectGeoByIp read the hash right here, the view is already set by the pin
                window.history.replaceState(window.history.state, '', formatMapHash(map));
            }
            const hash = createMapHash(map);

            window.__leafletMap = map;
            window.__leafletHash = hash;

            mapRef.current = map;
            if (!ctx.mapMarkerListener) {
                ctx.setMapMarkerListener(
                    () => (lat, lng) => updateMarker({ lat, lng, setHoverPoint, hoverPointRef, ctx })
                );
            }
            detectGeoByIp({ map, hash });
        }
    };

    useEffect(() => {
        if (isMvtTileURL(mtx.tileURL)) {
            return;
        }
        if (tileLayer.current) {
            const leafletLayer = tileLayer.current.getLeafletLayer();
            if (leafletLayer) {
                leafletLayer.setUrl(mtx.tileURL.url);
                window.seIsTilesLoaded = false;
            }
        }
    }, [mtx.tileURL]);

    const handlersRef = useRef({ handleLoad: null, leafletLayer: null });

    useEffect(() => {
        if (handlersRef.current.leafletLayer) return;

        if (tileLayer.current && tileLayer.current.getLeafletLayer) {
            const leafletLayer = tileLayer.current.getLeafletLayer();
            if (leafletLayer) {
                handlersRef.current.leafletLayer = leafletLayer;
                window.seIsTilesLoaded = false;
                handlersRef.current.handleLoad = () => {
                    window.seIsTilesLoaded = true;
                };
                leafletLayer.on('load', handlersRef.current.handleLoad);
            }
        }
    });

    useEffect(() => {
        return () => {
            const { leafletLayer, handleLoad } = handlersRef.current;
            if (leafletLayer && handleLoad) {
                leafletLayer.off('load', handleLoad);
                handlersRef.current.leafletLayer = null;
                handlersRef.current.handleLoad = null;
            }
        };
    }, []);

    // fix contextmenu of point for mobile device
    useEffect(() => {
        const markerEventHandler = (e) => {
            if (
                e.target?.draggable &&
                e.target?.alt === 'Marker' &&
                e.target?.classList?.contains('leaflet-marker-draggable')
            ) {
                if (e.type === 'touchstart') {
                    ctx.setPointContextMenu({});
                    e.preventDefault();
                }
                if (e.type === 'touchend') {
                    e.preventDefault();
                    if (e.target?.title !== 'poly') {
                        e.target?.dispatchEvent(new Event('contextmenu', e));
                    }
                }
            }
        };
        document.addEventListener('touchstart', markerEventHandler, { passive: false });
        document.addEventListener('touchend', markerEventHandler, { passive: false });

        return () => {
            document.removeEventListener('touchstart', markerEventHandler);
            document.removeEventListener('touchend', markerEventHandler);
        };
    }, [ctx]);

    const routersReady = ctx.trackRouter.isReady() && ctx.routeObject.isReady();

    return (
        <Box
            {...mapDragHandlers}
            sx={{
                width: '100%',
                height: '100%',
                position: 'fixed',
                '& .leaflet-control-layers': {
                    border: '0px !important',
                },
                '& .leaflet-bottom .leaflet-control-zoom': {
                    bottom: ctx.globalGraph?.show ? `${ctx.globalGraph.size}px` : '0px',
                },
                '& .leaflet-control-scale': {
                    marginLeft:
                        width < 750 || width - marginLeft < 500
                            ? `${(parseFloat(mainMenuWidth) || 0) + (parseFloat(menuInfoWidth) || 0) + 20}px`
                            : `${marginLeft}px`,
                    display: width > 750 && width - marginLeft > 500 ? 'inline-block' : 'flex',
                    float: 'none',
                    marginBottom: ctx.globalGraph?.show ? `${ctx.globalGraph.size + 20}px` : '20px',
                },
                '& .leaflet-control-attribution': {
                    left: `${(parseFloat(mainMenuWidth) || 0) + (parseFloat(menuInfoWidth) || 0)}px`,
                    marginLeft: '20px',
                    borderRadius: '4px !important',
                    marginBottom: ctx.globalGraph?.show ? `${ctx.globalGraph.size + 20}px` : '20px',
                },
                '& .leaflet-control-layers-toggle': {
                    width: '0px !important',
                    height: '0px !important',
                },
                '& .leaflet-bar': {
                    border: '4px !important',
                    width: '40px !important',
                },
                '& .leaflet-bar a': {
                    width: '40px !important',
                    color: '#757575',
                    border: '4px !important',
                },
            }}
        >
            <MapContainer
                zoom={initialView.zoom}
                center={initialView.center}
                minZoom={2}
                maxZoom={20}
                zoomSnap={0.01}
                scrollWheelZoom={false}
                worldCopyJump={true}
                zoomControl={false}
                whenReady={whenReadyHandler}
                contextmenu={true}
                contextmenuItems={[]}
                editable={true}
                attributionControl={false}
                style={{ width: '100%', height: '100%' }}
            >
                {routersReady && <CloudTrackLayer />}
                {routersReady && <LocalClientTrackLayer />}
                {routersReady && <NavigationLayer geocodingData={geocodingData} region={regionData} />}
                <TrackAnalyzerLayer />
                <ShareFileLayer />
                <TravelLayer />
                <HeatmapLayer />
                <FavoriteLayer />
                <WeatherLayer />
                <GraphLayer />
                <PoiLayer />
                <SearchLayer />
                <MapStateLayer />
                <ExploreLayer />
                <TransportStopsLayer />
                <CustomTileLayer
                    ref={tileLayer}
                    attribution='OsmAnd Web 1.03 &amp;copy <a href="https://osm.org/copyright" target="_blank">OpenStreetMap</a> contributors'
                    minZoom={2}
                    maxZoom={20}
                    maxNativeZoom={19}
                    updateWhenZooming={false}
                />
                <MvtDemoLayer />
                <MvtOsmLayer />
                <HeightmapLayer />
                {hoverPoint && (
                    <Marker ref={hoverPointRef} position={hoverPoint} icon={MarkerOptions.options.pointerGraph} />
                )}
                <ScaleControl position="bottomleft" imperial={false} />
                <AttributionControl position="bottomleft" prefix={false} />
                <LocationControl position="bottomright" />
                <ZoomControl position="bottomright" />
                <ContextMenu setGeocodingData={setGeocodingData} setRegionData={setRegionData} />
            </MapContainer>
        </Box>
    );
};

export default OsmAndMap;
